/**
 * Checkout Analytics Service
 * 
 * Comprehensive analytics tracking service for e-commerce conversion metrics.
 * Provides detailed tracking for the entire checkout funnel with support for
 * multiple analytics platforms and conversion optimization insights.
 * 
 * Features:
 * - E-commerce funnel tracking (view → add → checkout → purchase)
 * - Conversion rate optimization metrics
 * - Cart abandonment tracking
 * - Payment method performance analysis
 * - Revenue and transaction tracking
 * - User behavior analytics
 * - A/B testing support
 * - Multiple analytics platform support (GA4, custom analytics)
 */

import { ShopifyProduct, ShopifyProductVariant } from '../types/shopify';
import { CartItem } from '../contexts/ShoppingCartContext';
import { debugLog } from '../utils/debugLog';
import { resolveVariantPrice } from '../utils/shopifyHelpers';
import { analyticsEnv } from '../config/analyticsEnv';

// Analytics event types
export enum AnalyticsEvent {
  PAGE_VIEW = 'page_view',
  // Product Events
  PRODUCT_VIEW = 'view_item',
  PRODUCT_LIST_VIEW = 'view_item_list',
  PRODUCT_SEARCH = 'search',
  
  // Cart Events
  ADD_TO_CART = 'add_to_cart',
  REMOVE_FROM_CART = 'remove_from_cart',
  VIEW_CART = 'view_cart',
  
  // Checkout Events
  BEGIN_CHECKOUT = 'begin_checkout',
  ADD_PAYMENT_INFO = 'add_payment_info',
  ADD_SHIPPING_INFO = 'add_shipping_info',
  PURCHASE = 'purchase',
  
  // Custom Events
  CART_ABANDONMENT = 'cart_abandonment',
  CHECKOUT_ABANDONMENT = 'checkout_abandonment',
  PAYMENT_METHOD_SELECTED = 'payment_method_selected',
  CHECKOUT_ERROR = 'checkout_error',
  CHECKOUT_RETRY = 'checkout_retry',
  
  // Engagement Events
  SHARE_PRODUCT = 'share',
  WISHLIST_ADD = 'add_to_wishlist',
  NEWSLETTER_SIGNUP = 'newsletter_signup',
  CTA_INTERACTION = 'cta_interaction',

  // Listening Events
  SHOW_LISTEN_START = 'show_listen_start',
  SHOW_LISTEN_HEARTBEAT = 'show_listen_heartbeat',
  SHOW_LISTEN_STOP = 'show_listen_stop'
}

// Analytics platforms
export enum AnalyticsPlatform {
  GOOGLE_ANALYTICS = 'google_analytics',
  FACEBOOK_PIXEL = 'facebook_pixel',
  CUSTOM_ANALYTICS = 'custom_analytics',
  SHOPIFY_ANALYTICS = 'shopify_analytics'
}

// Product data for analytics
export interface AnalyticsProduct {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price: number;
  quantity: number;
  currency: string;
  item_brand?: string;
  item_list_name?: string;
  item_list_id?: string;
  index?: number;
}

// E-commerce data structure
export interface EcommerceData {
  transaction_id?: string;
  value: number;
  currency: string;
  items: AnalyticsProduct[];
  coupon?: string;
  shipping?: number;
  tax?: number;
  payment_type?: string;
}

// Conversion funnel step
export interface FunnelStep {
  step: number;
  step_name: string;
  timestamp: Date;
  value?: number;
  items?: AnalyticsProduct[];
}

// User session data
export interface SessionData {
  session_id: string;
  user_id?: string;
  device_type: 'mobile' | 'tablet' | 'desktop';
  traffic_source?: string;
  campaign?: string;
  referrer?: string;
}

// Analytics configuration
export interface AnalyticsConfig {
  enabledPlatforms: AnalyticsPlatform[];
  googleAnalyticsId?: string;
  facebookPixelId?: string;
  customEndpoint?: string;
  debugMode: boolean;
  gaDebugMode?: boolean;
  enableConversionTracking: boolean;
  enableFunnelTracking: boolean;
  enableAbandonmentTracking: boolean;
  abandonmentTimeoutMs: number;
  autoEnable: boolean;
  queueEventsUntilEnabled: boolean;
}

export interface PageViewMetadata {
  pageTitle?: string;
  pageLocation?: string;
  pagePath?: string;
  pageReferrer?: string;
  pageSearch?: string;
  pageHash?: string;
}

export interface ShowListeningAnalyticsPayload {
  sessionId: string;
  showId?: string | null;
  showSlug?: string | null;
  showTitle?: string | null;
  presenterName?: string | null;
  trackId?: string | null;
  trackTitle?: string | null;
  streamType?: string | null;
  playbackPositionSeconds?: number | null;
  listenSeconds?: number | null;
  durationSeconds?: number | null;
  sequence?: number | null;
}

/**
 * Checkout Analytics Service Class
 */
class CheckoutAnalyticsService {
  private config: AnalyticsConfig;
  private sessionData: SessionData;
  private funnelSteps: FunnelStep[] = [];
  private abandonmentTimers: Map<string, NodeJS.Timeout> = new Map();
  private analyticsEnabled = false;
  private googleAnalyticsScriptAttached = false;
  private googleAnalyticsConfigured = false;
  private analyticsEventQueue: Array<{ event: AnalyticsEvent; data?: unknown }> = [];
  private readonly maxQueuedEvents = 100;
  private allowEventQueue = true;

  private debug(...args: unknown[]): void {
    if (this.config.debugMode) {
      debugLog(...args);
    }
  }

  constructor(config: Partial<AnalyticsConfig> = {}) {
    const {
      gaDebugMode,
      autoEnable,
      queueEventsUntilEnabled,
      ...restConfig
    } = config;

    this.config = {
      enabledPlatforms: [AnalyticsPlatform.GOOGLE_ANALYTICS],
      debugMode: process.env.NODE_ENV === 'development',
      enableConversionTracking: true,
      enableFunnelTracking: true,
      enableAbandonmentTracking: true,
      abandonmentTimeoutMs: 30 * 60 * 1000, // 30 minutes
      gaDebugMode: gaDebugMode ?? false,
      autoEnable: autoEnable ?? true,
      queueEventsUntilEnabled: queueEventsUntilEnabled ?? true,
      ...restConfig
    };

    this.allowEventQueue = this.config.queueEventsUntilEnabled;
    this.sessionData = this.initializeSession();

    if (this.config.autoEnable) {
      this.enableAnalytics();
    }
  }

  /**
   * Initialize user session data
   */
  private initializeSession(): SessionData {
    const sessionId = this.generateSessionId();
    const deviceType = this.detectDeviceType();
    
    return {
      session_id: sessionId,
      device_type: deviceType,
      traffic_source: this.getTrafficSource(),
      referrer: document.referrer || undefined
    };
  }

  /**
   * Initialize analytics platforms
   */
  private initializeAnalyticsPlatforms(): void {
    if (!this.analyticsEnabled) {
      return;
    }

    if (this.config.enabledPlatforms.includes(AnalyticsPlatform.GOOGLE_ANALYTICS)) {
      this.ensureGoogleAnalyticsInitialized();
    }
    
    if (this.config.enabledPlatforms.includes(AnalyticsPlatform.FACEBOOK_PIXEL)) {
      this.initializeFacebookPixel();
    }
  }

  /**
   * Initialize Google Analytics 4 (idempotent)
   */
  private ensureGoogleAnalyticsInitialized(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const measurementId = this.config.googleAnalyticsId;
    if (!measurementId) {
      if (this.config.debugMode) {
        this.debug('[Analytics] Google Analytics ID missing; initialization skipped.');
      }
      return;
    }

    if (!this.googleAnalyticsScriptAttached) {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[data-eruption-ga="true"][data-ga-id="${measurementId}"]`
      );

      if (!existingScript) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
          measurementId
        )}`;
        script.setAttribute('data-eruption-ga', 'true');
        script.setAttribute('data-ga-id', measurementId);
        script.onerror = () => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
          this.googleAnalyticsScriptAttached = false;
          this.debug('[Analytics] Failed to load Google Analytics script.', { measurementId });
        };
        document.head.appendChild(script);
      }

      this.googleAnalyticsScriptAttached = true;
    }

    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer!.push(arguments);
      };
    }

    if (!this.googleAnalyticsConfigured) {
      window.gtag('js', new Date());
      this.googleAnalyticsConfigured = true;
    }

    window.gtag('config', measurementId, {
      send_page_view: false,
      currency: 'GBP',
      country: 'GB',
      ...(this.config.gaDebugMode ? { debug_mode: true } : {})
    });
  }

  /**
   * Initialize Facebook Pixel
   */
  private initializeFacebookPixel(): void {
    if (typeof window === 'undefined' || !this.config.facebookPixelId) return;

    // Facebook Pixel implementation would go here
    // For now, we'll just log that it would be initialized
    if (this.config.debugMode) {
      this.debug('[Analytics] Facebook Pixel would be initialized with ID:', this.config.facebookPixelId);
    }
  }

  enableAnalytics(): void {
    this.allowEventQueue = this.config.queueEventsUntilEnabled;
    if (this.analyticsEnabled) {
      this.initializeAnalyticsPlatforms();
      this.flushQueuedEvents();
      return;
    }

    this.analyticsEnabled = true;
    this.initializeAnalyticsPlatforms();
    this.flushQueuedEvents();
  }

  disableAnalytics(): void {
    this.allowEventQueue = false;
    if (!this.analyticsEnabled) {
      this.analyticsEventQueue = [];
      return;
    }

    this.analyticsEnabled = false;
    this.analyticsEventQueue = [];
  }

  private queueAnalyticsEvent(event: AnalyticsEvent, data?: any): void {
    if (!this.allowEventQueue || !this.config.queueEventsUntilEnabled) {
      return;
    }

    if (this.analyticsEventQueue.length >= this.maxQueuedEvents) {
      this.analyticsEventQueue.shift();
    }

    this.analyticsEventQueue.push({ event, data });
  }

  private flushQueuedEvents(): void {
    if (!this.analyticsEnabled || this.analyticsEventQueue.length === 0) {
      return;
    }

    const queuedEvents = [...this.analyticsEventQueue];
    this.analyticsEventQueue = [];

    queuedEvents.forEach(({ event, data }) => {
      this.dispatchAnalyticsEvent(event, data);
    });
  }

  private dispatchAnalyticsEvent(event: AnalyticsEvent, data?: any): void {
    if (this.config.enabledPlatforms.includes(AnalyticsPlatform.GOOGLE_ANALYTICS)) {
      this.trackToGoogleAnalytics(event, data);
    }

    if (this.config.enabledPlatforms.includes(AnalyticsPlatform.FACEBOOK_PIXEL)) {
      this.trackToFacebookPixel(event, data);
    }

    if (this.config.enabledPlatforms.includes(AnalyticsPlatform.CUSTOM_ANALYTICS)) {
      this.trackToCustomAnalytics(event, data);
    }
  }

  /**
   * Track product view
   */
  trackProductView(product: ShopifyProduct, listName?: string): void {
    const analyticsProduct = this.convertToAnalyticsProduct(product);
    
    const eventData: EcommerceData = {
      currency: 'GBP',
      value: parseFloat(product.priceRange.minVariantPrice.amount),
      items: [analyticsProduct]
    };

    if (listName) {
      analyticsProduct.item_list_name = listName;
    }

    this.trackEvent(AnalyticsEvent.PRODUCT_VIEW, eventData);
    
    if (this.config.enableFunnelTracking) {
      this.addFunnelStep(1, 'Product View', parseFloat(product.priceRange.minVariantPrice.amount), [analyticsProduct]);
    }
  }

  /**
   * Track product list view
   */
  trackProductListView(products: ShopifyProduct[], listName: string): void {
    const analyticsProducts = products.map((product, index) => ({
      ...this.convertToAnalyticsProduct(product),
      item_list_name: listName,
      index: index + 1
    }));

    const totalValue = products.reduce((sum, product) => 
      sum + parseFloat(product.priceRange.minVariantPrice.amount), 0
    );

    const eventData: EcommerceData = {
      currency: 'GBP',
      value: totalValue,
      items: analyticsProducts
    };

    this.trackEvent(AnalyticsEvent.PRODUCT_LIST_VIEW, eventData);
  }

  /**
   * Track add to cart
   */
  trackAddToCart(product: ShopifyProduct, variant: ShopifyProductVariant, quantity: number = 1): void {
    const analyticsProduct = this.convertToAnalyticsProduct(product, variant, quantity);
    const { amount: unitPrice, currencyCode } = resolveVariantPrice(variant, product);
    
    const eventData: EcommerceData = {
      currency: currencyCode,
      value: unitPrice * quantity,
      items: [analyticsProduct]
    };

    this.trackEvent(AnalyticsEvent.ADD_TO_CART, eventData);
    
    if (this.config.enableFunnelTracking) {
      this.addFunnelStep(2, 'Add to Cart', eventData.value, [analyticsProduct]);
    }

    // Clear any existing abandonment timer for this product
    this.clearAbandonmentTimer('cart');
  }

  /**
   * Track remove from cart
   */
  trackRemoveFromCart(item: CartItem): void {
    const { amount: unitPrice, currencyCode } = resolveVariantPrice(item.variant, item.product);
    const analyticsProduct: AnalyticsProduct = {
      item_id: item.variantId,
      item_name: item.product.title,
      price: unitPrice,
      quantity: item.quantity,
      currency: currencyCode
    };

    const eventData: EcommerceData = {
      currency: currencyCode,
      value: unitPrice * item.quantity,
      items: [analyticsProduct]
    };

    this.trackEvent(AnalyticsEvent.REMOVE_FROM_CART, eventData);
  }

  /**
   * Track view cart
   */
  trackViewCart(items: CartItem[]): void {
    const analyticsProducts = items.map(item => {
      const { amount: unitPrice, currencyCode } = resolveVariantPrice(item.variant, item.product);
      return {
        item_id: item.variantId,
        item_name: item.product.title,
        price: unitPrice,
        quantity: item.quantity,
        currency: currencyCode
      };
    });

    const totalValue = items.reduce((sum, item) => {
      const { amount: unitPrice } = resolveVariantPrice(item.variant, item.product);
      return sum + (unitPrice * item.quantity);
    }, 0);

    const eventData: EcommerceData = {
      currency: analyticsProducts[0]?.currency || 'GBP',
      value: totalValue,
      items: analyticsProducts
    };

    this.trackEvent(AnalyticsEvent.VIEW_CART, eventData);
    
    // Start cart abandonment timer
    if (this.config.enableAbandonmentTracking) {
      this.startAbandonmentTimer('cart', eventData);
    }
  }

  /**
   * Track begin checkout
   */
  trackBeginCheckout(items: CartItem[]): void {
    const analyticsProducts = items.map(item => {
      const { amount: unitPrice, currencyCode } = resolveVariantPrice(item.variant, item.product);
      return {
        item_id: item.variantId,
        item_name: item.product.title,
        price: unitPrice,
        quantity: item.quantity,
        currency: currencyCode
      };
    });

    const totalValue = items.reduce((sum, item) => {
      const { amount: unitPrice } = resolveVariantPrice(item.variant, item.product);
      return sum + (unitPrice * item.quantity);
    }, 0);

    const eventData: EcommerceData = {
      currency: analyticsProducts[0]?.currency || 'GBP',
      value: totalValue,
      items: analyticsProducts
    };

    this.trackEvent(AnalyticsEvent.BEGIN_CHECKOUT, eventData);
    
    if (this.config.enableFunnelTracking) {
      this.addFunnelStep(3, 'Begin Checkout', totalValue, analyticsProducts);
    }

    // Clear cart abandonment and start checkout abandonment timer
    this.clearAbandonmentTimer('cart');
    if (this.config.enableAbandonmentTracking) {
      this.startAbandonmentTimer('checkout', eventData);
    }
  }

  /**
   * Track payment method selection
   */
  trackPaymentMethodSelected(paymentMethod: string, items: CartItem[]): void {
    const totalValue = items.reduce((sum, item) => {
      const { amount: unitPrice } = resolveVariantPrice(item.variant, item.product);
      return sum + (unitPrice * item.quantity);
    }, 0);

    this.trackEvent(AnalyticsEvent.PAYMENT_METHOD_SELECTED, {
      payment_method: paymentMethod,
      value: totalValue,
      currency: 'GBP'
    });

    this.trackEvent(AnalyticsEvent.ADD_PAYMENT_INFO, {
      currency: items[0] ? resolveVariantPrice(items[0].variant, items[0].product).currencyCode : 'GBP',
      value: totalValue,
      payment_type: paymentMethod
    });
  }

  /**
   * Track purchase completion
   */
  trackPurchase(transactionId: string, items: CartItem[], totalAmount: number, tax?: number, shipping?: number): void {
    const analyticsProducts = items.map(item => {
      const { amount: unitPrice, currencyCode } = resolveVariantPrice(item.variant, item.product);
      return {
        item_id: item.variantId,
        item_name: item.product.title,
        price: unitPrice,
        quantity: item.quantity,
        currency: currencyCode
      };
    });

    const eventData: EcommerceData = {
      transaction_id: transactionId,
      currency: analyticsProducts[0]?.currency || 'GBP',
      value: totalAmount,
      items: analyticsProducts,
      tax,
      shipping
    };

    this.trackEvent(AnalyticsEvent.PURCHASE, eventData);
    
    if (this.config.enableFunnelTracking) {
      this.addFunnelStep(4, 'Purchase', totalAmount, analyticsProducts);
    }

    // Clear all abandonment timers
    this.clearAllAbandonmentTimers();

    // Track conversion completion
    this.trackConversionCompletion(eventData);
  }

  /**
   * Track checkout error
   */
  trackCheckoutError(errorType: string, errorMessage: string, step?: string): void {
    this.trackEvent(AnalyticsEvent.CHECKOUT_ERROR, {
      error_type: errorType,
      error_message: errorMessage,
      checkout_step: step,
      session_id: this.sessionData.session_id
    });
  }

  /**
   * Track checkout retry
   */
  trackCheckoutRetry(retryCount: number, errorType?: string): void {
    this.trackEvent(AnalyticsEvent.CHECKOUT_RETRY, {
      retry_count: retryCount,
      error_type: errorType,
      session_id: this.sessionData.session_id
    });
  }

  /**
   * Track checkout initiation (successful redirect to Shopify)
   */
  trackCheckoutInitiation(items: CartItem[]): void {
    const totalValue = items.reduce((sum, item) => {
      const { amount: unitPrice } = resolveVariantPrice(item.variant, item.product);
      return sum + (unitPrice * item.quantity);
    }, 0);

    this.trackEvent('checkout_initiated' as AnalyticsEvent, {
      items_count: items.length,
      total_value: totalValue,
      currency: items[0] ? resolveVariantPrice(items[0].variant, items[0].product).currencyCode : 'GBP',
      session_id: this.sessionData.session_id
    });
  }

  /**
   * Track search
   */
  trackSearch(searchTerm: string, resultsCount?: number): void {
    this.trackEvent(AnalyticsEvent.PRODUCT_SEARCH, {
      search_term: searchTerm,
      results_count: resultsCount
    });
  }

  /**
   * Convert Shopify product to analytics format
   */
  private convertToAnalyticsProduct(
    product: ShopifyProduct, 
    variant?: ShopifyProductVariant, 
    quantity: number = 1
  ): AnalyticsProduct {
    const selectedVariant = variant || product.variants.edges[0]?.node;
    const { amount: unitPrice, currencyCode } = resolveVariantPrice(selectedVariant, product);
    
    return {
      item_id: selectedVariant?.id || product.id,
      item_name: product.title,
      item_category: product.productType || undefined,
      item_variant: selectedVariant?.title !== 'Default Title' ? selectedVariant?.title : undefined,
      price: unitPrice,
      quantity,
      currency: currencyCode,
      item_brand: product.vendor || 'Eruption Radio'
    };
  }

  /**
   * Track event to all enabled platforms
   */
  trackNewsletterSignup(status: 'success' | 'error', metadata?: { source?: string; email?: string; error?: string }): void {
    const emailDomain = metadata?.email ? metadata.email.split('@')[1] || undefined : undefined;
    this.trackEvent(AnalyticsEvent.NEWSLETTER_SIGNUP, {
      status,
      source: metadata?.source || 'unspecified',
      email_domain: emailDomain,
      error: metadata?.error,
      timestamp: new Date().toISOString()
    });
  }

  trackCtaInteraction(id: string, location?: string): void {
    this.trackEvent(AnalyticsEvent.CTA_INTERACTION, {
      cta_id: id,
      location: location || 'unspecified',
      timestamp: new Date().toISOString()
    });
  }

  trackPageView(pathname: string, metadata: PageViewMetadata = {}): void {
    if (!pathname) {
      return;
    }

    const pageLocation =
      metadata.pageLocation ??
      (typeof window !== 'undefined'
        ? `${window.location.origin ?? ''}${pathname}${metadata.pageSearch ?? window.location.search ?? ''}${metadata.pageHash ?? ''}`
        : undefined);

    const pageData = this.filterUndefined({
      page_location: pageLocation,
      page_path: metadata.pagePath ?? pathname,
      page_title:
        metadata.pageTitle ?? (typeof document !== 'undefined' ? document.title || undefined : undefined),
      page_referrer:
        metadata.pageReferrer ??
        (typeof document !== 'undefined' ? document.referrer || undefined : undefined),
      page_search: metadata.pageSearch ?? (typeof window !== 'undefined' ? window.location.search || undefined : undefined),
      page_hash: metadata.pageHash ?? (typeof window !== 'undefined' ? window.location.hash || undefined : undefined)
    });

    this.trackEvent(AnalyticsEvent.PAGE_VIEW, pageData);
  }

  trackShowListeningEvent(
    eventType: 'start' | 'heartbeat' | 'stop',
    payload: ShowListeningAnalyticsPayload
  ): void {
    const eventMap: Record<typeof eventType, AnalyticsEvent> = {
      start: AnalyticsEvent.SHOW_LISTEN_START,
      heartbeat: AnalyticsEvent.SHOW_LISTEN_HEARTBEAT,
      stop: AnalyticsEvent.SHOW_LISTEN_STOP
    };

    const eventPayload = this.filterUndefined({
      session_id: payload.sessionId,
      show_id: payload.showId || undefined,
      show_slug: payload.showSlug || undefined,
      show_title: payload.showTitle || undefined,
      presenter_name: payload.presenterName || undefined,
      track_id: payload.trackId || undefined,
      track_title: payload.trackTitle || undefined,
      stream_type: payload.streamType || undefined,
      playback_position_seconds: this.sanitizeNumberForAnalytics(payload.playbackPositionSeconds),
      listen_seconds: this.sanitizeNumberForAnalytics(payload.listenSeconds),
      duration_seconds: this.sanitizeNumberForAnalytics(payload.durationSeconds),
      sequence_number: this.sanitizeNumberForAnalytics(payload.sequence)
    });

    this.trackEvent(eventMap[eventType], eventPayload);
  }

  private trackEvent(event: AnalyticsEvent, data?: any): void {
    if (this.config.debugMode) {
      this.debug(`[Analytics] ${event}:`, data);
    }

    if (!this.analyticsEnabled) {
      this.queueAnalyticsEvent(event, data);
      return;
    }

    this.dispatchAnalyticsEvent(event, data);
  }

  private sanitizeNumberForAnalytics(value?: number | null): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return undefined;
    }

    if (numeric < 0) {
      return 0;
    }

    return Math.floor(numeric);
  }

  private filterUndefined<T extends Record<string, unknown>>(data: T): T {
    const filteredEntries = Object.entries(data).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    );
    return Object.fromEntries(filteredEntries) as T;
  }

  /**
   * Track to Google Analytics 4
   */
  private trackToGoogleAnalytics(event: AnalyticsEvent, data?: any): void {
    if (typeof window === 'undefined') return;

    this.ensureGoogleAnalyticsInitialized();

    if (!window.gtag) return;

    try {
      window.gtag('event', event, {
        ...data,
        session_id: this.sessionData.session_id,
        device_type: this.sessionData.device_type,
        traffic_source: this.sessionData.traffic_source
      });
    } catch (error) {
      this.debug('[Analytics] Google Analytics tracking failed:', error);
    }
  }

  /**
   * Track to Facebook Pixel
   */
  private trackToFacebookPixel(event: AnalyticsEvent, data?: any): void {
    // Facebook Pixel implementation would go here
    if (this.config.debugMode) {
      this.debug('[Analytics] Facebook Pixel event:', event, data);
    }
  }

  /**
   * Track to custom analytics endpoint
   */
  private trackToCustomAnalytics(event: AnalyticsEvent, data?: any): void {
    if (!this.config.customEndpoint) return;

    try {
      fetch(this.config.customEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event,
          data,
          session: this.sessionData,
          timestamp: new Date().toISOString()
        })
      }).catch(error => {
        this.debug('[Analytics] Custom analytics tracking failed:', error);
      });
    } catch (error) {
      this.debug('[Analytics] Custom analytics tracking failed:', error);
    }
  }

  /**
   * Add funnel step
   */
  private addFunnelStep(step: number, stepName: string, value?: number, items?: AnalyticsProduct[]): void {
    this.funnelSteps.push({
      step,
      step_name: stepName,
      timestamp: new Date(),
      value,
      items
    });

    // Keep only last 10 steps to prevent memory issues
    if (this.funnelSteps.length > 10) {
      this.funnelSteps = this.funnelSteps.slice(-10);
    }
  }

  /**
   * Start abandonment timer
   */
  private startAbandonmentTimer(type: 'cart' | 'checkout', eventData: EcommerceData): void {
    this.clearAbandonmentTimer(type);

    const timer = setTimeout(() => {
      this.trackEvent(
        type === 'cart' ? AnalyticsEvent.CART_ABANDONMENT : AnalyticsEvent.CHECKOUT_ABANDONMENT,
        {
          ...eventData,
          abandonment_time_minutes: this.config.abandonmentTimeoutMs / (1000 * 60),
          session_id: this.sessionData.session_id
        }
      );
    }, this.config.abandonmentTimeoutMs);

    this.abandonmentTimers.set(type, timer);
  }

  /**
   * Clear abandonment timer
   */
  private clearAbandonmentTimer(type: 'cart' | 'checkout'): void {
    const timer = this.abandonmentTimers.get(type);
    if (timer) {
      clearTimeout(timer);
      this.abandonmentTimers.delete(type);
    }
  }

  /**
   * Clear all abandonment timers
   */
  private clearAllAbandonmentTimers(): void {
    this.abandonmentTimers.forEach(timer => clearTimeout(timer));
    this.abandonmentTimers.clear();
  }

  /**
   * Track conversion completion
   */
  private trackConversionCompletion(purchaseData: EcommerceData): void {
    if (!this.config.enableConversionTracking) return;

    // Calculate conversion metrics
    const conversionData = {
      ...purchaseData,
      funnel_steps: this.funnelSteps.length,
      time_to_conversion: this.calculateTimeToConversion(),
      session_id: this.sessionData.session_id,
      device_type: this.sessionData.device_type
    };

    // Track to analytics platforms
    this.trackEvent('conversion_completed' as AnalyticsEvent, conversionData);
  }

  /**
   * Calculate time to conversion
   */
  private calculateTimeToConversion(): number {
    if (this.funnelSteps.length < 2) return 0;
    
    const firstStep = this.funnelSteps[0];
    const lastStep = this.funnelSteps[this.funnelSteps.length - 1];
    
    return lastStep.timestamp.getTime() - firstStep.timestamp.getTime();
  }

  /**
   * Generate session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Detect device type
   */
  private detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (typeof window === 'undefined') return 'desktop';
    
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * Get traffic source
   */
  private getTrafficSource(): string {
    if (typeof window === 'undefined') return 'direct';
    
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    
    if (utmSource) return utmSource;
    if (document.referrer) {
      try {
        const referrerDomain = new URL(document.referrer).hostname;
        return referrerDomain;
      } catch {
        return 'referrer';
      }
    }
    
    return 'direct';
  }

  /**
   * Get funnel conversion data
   */
  getFunnelData(): FunnelStep[] {
    return [...this.funnelSteps];
  }

  /**
   * Get session data
   */
  getSessionData(): SessionData {
    return { ...this.sessionData };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<AnalyticsConfig>): void {
    if (!newConfig || Object.keys(newConfig).length === 0) {
      return;
    }

    const previousConfig = { ...this.config };

    this.config = {
      ...this.config,
      ...newConfig,
      gaDebugMode: newConfig.gaDebugMode ?? previousConfig.gaDebugMode,
      autoEnable: newConfig.autoEnable ?? previousConfig.autoEnable,
      queueEventsUntilEnabled:
        newConfig.queueEventsUntilEnabled ?? previousConfig.queueEventsUntilEnabled
    };

    this.allowEventQueue = this.analyticsEnabled || this.config.autoEnable;

    if (
      newConfig.googleAnalyticsId &&
      newConfig.googleAnalyticsId !== previousConfig.googleAnalyticsId
    ) {
      this.googleAnalyticsScriptAttached = false;
      this.googleAnalyticsConfigured = false;
    }

    if (this.analyticsEnabled) {
      this.allowEventQueue = this.config.queueEventsUntilEnabled;
      this.initializeAnalyticsPlatforms();
    } else if (this.config.autoEnable) {
      this.allowEventQueue = this.config.queueEventsUntilEnabled;
      this.enableAnalytics();
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): AnalyticsConfig {
    return { ...this.config };
  }
}

// Global type declarations for analytics platforms
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    fbq?: (...args: any[]) => void;
    analytics?: {
      track: (event: string, properties?: any) => void;
    };
  }
}

// Create and export singleton instance
export const checkoutAnalyticsService = new CheckoutAnalyticsService({
  enabledPlatforms: [AnalyticsPlatform.GOOGLE_ANALYTICS],
  googleAnalyticsId: analyticsEnv.measurementId,
  gaDebugMode: analyticsEnv.debugView,
  debugMode: process.env.NODE_ENV === 'development',
  enableConversionTracking: true,
  enableFunnelTracking: true,
  enableAbandonmentTracking: true,
  autoEnable: analyticsEnv.shouldAutoEnable
});

export default checkoutAnalyticsService;
export { CheckoutAnalyticsService };
